import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageChrome from '../layout/PageChrome';
import MemberCard from '../cards/MemberCard';
import { MemberCardSkeleton } from '../skeleton/SkeletonSystem';
import { FaEnvelope, FaInstagram } from 'react-icons/fa';
import RichText from '../ui/RichText';
import getInitials from '../../utils/getInitials';
import '../../css/common/MemberCard.css';

const MemberGridPage = ({
  members,
  title,
  subtitle,
  introText,
  baseRoute,
  paramName = 'memberId',
  loadingDelay = 2000
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const memberId = params[paramName];
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalImageFailed, setModalImageFailed] = useState(false);

  // Reset the image-failed flag whenever a different member is opened.
  useEffect(() => {
    setModalImageFailed(false);
  }, [selectedMember]);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, loadingDelay);

    return () => clearTimeout(timer);
  }, [loadingDelay]);

  // Check if URL has a member ID and open modal
  useEffect(() => {
    if (memberId && !loading) {
      const member = members.find(m => m.id === memberId);
      if (member) {
        setSelectedMember(member);
      } else {
        navigate(baseRoute, { replace: true });
      }
    }
  }, [memberId, loading, navigate, members, baseRoute]);

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    navigate(`${baseRoute}/${member.id}`);
  };

  const closeModal = () => {
    setSelectedMember(null);
    navigate(baseRoute);
  };

  return (
    <>
      <PageChrome title={title} subtitle={subtitle} intro={introText}>
        <section className="member-page-section">
          <div className="member-page-grid">
            {loading ? (
              Array.from({ length: members.length }, (_, index) => (
                <div key={`skeleton-${index}`} className="member-page-card-wrapper">
                  <MemberCardSkeleton />
                </div>
              ))
            ) : (
              members.map((member, index) => (
                <div 
                  key={member.id}
                  onClick={() => handleMemberClick(member)}
                  className="member-page-card-wrapper"
                >
                  <MemberCard 
                    name={member.name}
                    title={member.title}
                    bio={member.bio}
                    imageUrl={member.imageUrl}
                    index={index}
                  />
                </div>
              ))
            )}
          </div>
        </section>
      </PageChrome>

      {/* Member Modal */}
      {selectedMember && (
        <div className="member-modal-overlay" onClick={closeModal}>
          <div className="member-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="member-modal-close" onClick={closeModal}>×</button>
            <div className="member-modal-header">
              {(!selectedMember.imageUrl || modalImageFailed) ? (
                <div className="member-modal-monogram" role="img" aria-label={selectedMember.name}>
                  {getInitials(selectedMember.name)}
                </div>
              ) : (
                <img
                  src={selectedMember.imageUrl}
                  alt={selectedMember.name}
                  className="member-modal-headshot"
                  onError={() => setModalImageFailed(true)}
                />
              )}
              <div className="member-modal-info">
                <h2 className="member-modal-name">{selectedMember.name}</h2>
                <p className="member-modal-title">{selectedMember.title}</p>
                <div className="member-modal-social-links">
                  {selectedMember.email && (
                    <>
                      {Array.isArray(selectedMember.email) ? (
                        selectedMember.email.map((email, index) => (
                          <a 
                            key={index}
                            href={`mailto:${email}`} 
                            className="member-modal-social-link member-modal-email-link"
                            title={`Email ${selectedMember.name} (${email})`}
                          >
                            <FaEnvelope />
                          </a>
                        ))
                      ) : (
                        <a 
                          href={`mailto:${selectedMember.email}`} 
                          className="member-modal-social-link member-modal-email-link"
                          title={`Email ${selectedMember.name}`}
                        >
                          <FaEnvelope />
                        </a>
                      )}
                    </>
                  )}
                  {selectedMember.instagram && (
                    <a 
                      href={`https://www.instagram.com/${selectedMember.instagram}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="member-modal-social-link member-modal-instagram-link"
                      title={`Follow ${selectedMember.name} on Instagram`}
                    >
                      <FaInstagram />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="member-modal-body">
              <RichText value={selectedMember.bio} className="member-modal-bio" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MemberGridPage;
